if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.restoreHandler = this.reload.bind(this);
      this.resultClickHandler = this.handleResultClick.bind(this);
      this.loadMoreClickHandler = this.handleLoadMoreClick.bind(this);
      this.pageHideHandler = this.cancel.bind(this);
    }

    connectedCallback() {
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.restoreHandler);
      window.addEventListener('pagehide', this.pageHideHandler);
    }

    disconnectedCallback() {
      this.destroy();
      document.removeEventListener('on:bfcache:load-restore', this.restoreHandler);
      window.removeEventListener('pagehide', this.pageHideHandler);
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.feedbackTemplate = this.querySelector('.js-pagination-feedback');
      this.statusMessage = this.querySelector('.js-pagination-status');
      this.section = this.closest('[id^="shopify-section-"]');
      this.isFetching = false;
      this.controller = null;
      this.addEventListener('click', this.resultClickHandler);
      this.bindButton();
      this.setupPrefetch();
      if (this.infiniteScroll && this.hasMore()) {
        this.paginationObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            clearInterval(this.intersectingTimer);
            if (entry.isIntersecting) {
              this.loadMore();
              this.intersectingTimer = setInterval(() => this.loadMore(), 1000);
            }
          });
        }, { rootMargin: `${window.innerHeight * 1.5}px 0px` });
        this.paginationObserver.observe(this.pagination);
      }
    }

    hasMore() {
      const value = this.pagination?.dataset.isMoreResults;
      return Boolean(value && value !== 'false' && this.loadMoreButton?.hasAttribute('href'));
    }

    stopObserver() {
      clearInterval(this.intersectingTimer);
      this.paginationObserver?.disconnect();
      this.paginationObserver = null;
    }

    cancel() {
      this.stopPrefetch();
      this.prefetch?.controller.abort();
      this.prefetch = null;
      this.controller?.abort();
      this.controller = null;
      this.isFetching = false;
      this.loadMoreButton?.classList.remove('is-loading');
      this.loadMoreButton?.removeAttribute('aria-disabled');
      this.pagination?.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }

    clearError() {
      this.errorMessage?.remove();
      this.errorMessage = null;
    }

    destroy() {
      this.clearError();
      this.stopObserver();
      this.cancel();
      this.loadMoreButton?.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
    }

    reload() {
      this.destroy();
      if (this.isConnected) this.init();
    }

    bindButton() {
      this.loadMoreButton?.addEventListener('click', this.loadMoreClickHandler);
    }

    handleLoadMoreClick(evt) {
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      evt.preventDefault();
      this.loadMore(evt.detail === 0);
    }

    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result')) this.cancel();
    }

    nextPageURL() {
      const url = new URL(this.loadMoreButton.href);
      if (this.section) url.searchParams.set('section_id', this.section.id.slice('shopify-section-'.length));
      return url.toString();
    }

    async fetchPage(url, signal, priority = 'auto') {
      const response = await fetch(url, { signal, priority });
      if (!response.ok) throw new Error(`Pagination request failed: ${response.status}`);
      return response.text();
    }

    stopPrefetch() {
      this.prefetchObserver?.disconnect();
      this.prefetchObserver = null;
      if (this.prefetchIdle != null) window.cancelIdleCallback?.(this.prefetchIdle);
      clearTimeout(this.prefetchTimer);
      this.prefetchIdle = null;
      this.prefetchTimer = null;
      if (this.prefetchLoadHandler) window.removeEventListener('load', this.prefetchLoadHandler);
      this.prefetchLoadHandler = null;
    }

    setupPrefetch() {
      this.stopPrefetch();
      if (this.dataset.paginationPrefetch !== 'true' || this.infiniteScroll || !this.hasMore()
        || this.pagination.dataset.paginationStyle !== 'modern' || typeof IntersectionObserver === 'undefined') return;
      const observe = () => {
        this.prefetchLoadHandler = null;
        if (!this.isConnected || !this.loadMoreButton) return;
        this.prefetchObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            if (this.prefetchIdle != null) window.cancelIdleCallback?.(this.prefetchIdle);
            clearTimeout(this.prefetchTimer);
            this.prefetchIdle = null;
            this.prefetchTimer = null;
            return;
          }
          if (this.prefetch || this.prefetchIdle != null || this.prefetchTimer != null) return;
          const start = () => {
            this.prefetchIdle = null;
            this.prefetchTimer = null;
            this.prefetchNext();
          };
          if (window.requestIdleCallback) this.prefetchIdle = window.requestIdleCallback(start, { timeout: 1500 });
          else this.prefetchTimer = setTimeout(start, 200);
        }, { rootMargin: `${Math.min(window.innerHeight, 800)}px 0px` });
        this.prefetchObserver.observe(this.loadMoreButton);
      };
      if (document.readyState === 'complete') observe();
      else {
        this.prefetchLoadHandler = observe;
        window.addEventListener('load', observe, { once: true });
      }
    }

    prefetchNext() {
      const connection = window.navigator?.connection;
      if (this.prefetch || this.isFetching || !this.isConnected || !this.hasMore()
        || document.visibilityState === 'hidden' || this.dataset.pauseInfiniteScroll === 'true'
        || this.dataset.memoryRestoring === 'true' || connection?.saveData
        || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return;
      const controller = new AbortController();
      const url = this.nextPageURL();
      this.prefetch = { url, controller, startedAt: Date.now(),
        promise: this.fetchPage(url, controller.signal, 'low').catch(() => null) };
    }

    async loadMore(keyboard = false) {
      if (this.isFetching || this.dataset.pauseInfiniteScroll === 'true' || !this.hasMore()) return;
      this.clearError();
      const url = this.nextPageURL();
      const cached = this.prefetch;
      const reusable = cached?.url === url && !cached.controller.signal.aborted
        && Date.now() - cached.startedAt < 30000;
      this.stopPrefetch();
      this.prefetch = null;
      if (!reusable) cached?.controller.abort();
      const controller = reusable ? cached.controller : new AbortController();
      const pagination = this.pagination;
      const button = this.loadMoreButton;
      const hadFocus = document.activeElement === button;
      this.controller = controller;
      this.isFetching = true;
      this.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      if (pagination.dataset.paginationStyle === 'traditional') pagination.classList.add('is-loading');

      try {
        let html = reusable ? await cached.promise : await this.fetchPage(url, controller.signal);
        if (!html && !controller.signal.aborted) html = await this.fetchPage(url, controller.signal);
        if (controller.signal.aborted || this.controller !== controller || !this.isConnected
          || this.dataset.pauseInfiniteScroll === 'true') return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        const sourceSection = this.section ? tmpl.content.getElementById(this.section.id) : tmpl.content;
        const source = sourceSection?.querySelector('custom-pagination');
        if (!source) throw new Error('Pagination section missing');
        const nextPagination = source.querySelector('.js-pagination');
        const incoming = [...source.querySelectorAll('.js-pagination-result')];
        if (!incoming.length || !nextPagination) throw new Error('Pagination results changed');
        const lastResult = this.results[this.results.length - 1];
        if (!lastResult) throw new Error('Pagination target missing');

        // Initial document pages can prioritise their first images. Appended
        // batches, including memory reconstruction, keep native lazy loading.
        incoming.forEach(result => result.querySelectorAll('img').forEach(image => {
          if (image.getAttribute('loading') === 'eager') image.setAttribute('loading', 'lazy');
          if (image.getAttribute('fetchpriority') === 'high') image.removeAttribute('fetchpriority');
        }));
        lastResult.insertAdjacentHTML('afterend', incoming.map((result) => result.outerHTML).join(''));
        this.results = this.querySelectorAll('.js-pagination-result');
        const firstNewResult = this.results[this.results.length - incoming.length];
        button.removeEventListener('click', this.loadMoreClickHandler);
        pagination.innerHTML = nextPagination.innerHTML;
        pagination.dataset.isMoreResults = nextPagination.dataset.isMoreResults || '';
        this.loadMoreButton = this.querySelector('.js-pagination-load-more');
        this.bindButton();

        const message = pagination.querySelector('.js-pagination-message');
        if (message && this.hasMore()) {
          message.textContent = pagination.dataset.countMessage
            .replaceAll('[visible]', nextPagination.dataset.endIndex)
            .replaceAll('[total]', nextPagination.dataset.totalResults);
        }
        if (this.statusMessage && message) this.statusMessage.textContent = message.textContent;
        if (!this.hasMore()) {
          this.stopObserver();
          this.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
        }
        // Collection memory tracks batches without changing the URL or caching product HTML.
        if (this.hasAttribute('data-collection-memory')) {
          this.dataset.memoryEndPage = source.dataset.memoryEndPage;
          this.dispatchEvent(new CustomEvent('collection:page-loaded'));
        }
        if (keyboard && firstNewResult) {
          firstNewResult.setAttribute('tabindex', '-1');
          firstNewResult.focus({ preventScroll: true });
          firstNewResult.addEventListener('blur', () => firstNewResult.removeAttribute('tabindex'), { once: true });
        } else if (hadFocus) {
          (this.loadMoreButton || pagination).focus({ preventScroll: true });
        }
        return true;
      } catch (error) {
        if (controller.signal.aborted || this.controller !== controller) return;
        this.stopObserver();
        const feedback = this.feedbackTemplate?.content.firstElementChild?.cloneNode(true);
        if (feedback) {
          feedback.querySelector('.js-pagination-retry').addEventListener('click', (evt) => {
            this.loadMore(evt.detail === 0);
          }, { once: true });
          this.errorMessage = feedback;
          pagination.after(feedback);
        } else {
          console.warn('Unable to fetch next page of results', error);
        }
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.isFetching = false;
          this.removeAttribute('aria-busy');
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
          pagination.classList.remove('is-loading');
          if (!this.errorMessage) this.setupPrefetch();
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}

